/**
 * Locale Store
 * Manages language, currency, and country preferences
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n from '../i18n';

// Currency configurations
export const CURRENCIES = {
  EUR: { symbol: '€', code: 'EUR', name: 'Euro', position: 'after' },
  USD: { symbol: '$', code: 'USD', name: 'US Dollar', position: 'before' },
  GBP: { symbol: '£', code: 'GBP', name: 'British Pound', position: 'before' },
  BRL: { symbol: 'R$', code: 'BRL', name: 'Brazilian Real', position: 'before' },
};

// Country configurations with default currency and language
export const COUNTRIES = {
  PT: { code: 'PT', name: 'Portugal', currency: 'EUR', language: 'pt', flag: '🇵🇹' },
  BR: { code: 'BR', name: 'Brasil', currency: 'BRL', language: 'pt-BR', flag: '🇧🇷' },
  US: { code: 'US', name: 'United States', currency: 'USD', language: 'en', flag: '🇺🇸' },
  GB: { code: 'GB', name: 'United Kingdom', currency: 'GBP', language: 'en', flag: '🇬🇧' },
  ES: { code: 'ES', name: 'España', currency: 'EUR', language: 'es', flag: '🇪🇸' },
  FR: { code: 'FR', name: 'France', currency: 'EUR', language: 'fr', flag: '🇫🇷' },
};

// Language configurations
export const LANGUAGES = {
  'pt': { code: 'pt', name: 'Português (PT)', flag: '🇵🇹' },
  'pt-BR': { code: 'pt-BR', name: 'Português (BR)', flag: '🇧🇷' },
  'en': { code: 'en', name: 'English', flag: '🇬🇧' },
  'es': { code: 'es', name: 'Español', flag: '🇪🇸' },
  'fr': { code: 'fr', name: 'Français', flag: '🇫🇷' },
};

// Exchange rates (base: EUR) - These should ideally come from an API
const EXCHANGE_RATES = {
  EUR: 1,
  USD: 1.08,
  GBP: 0.86,
  BRL: 5.45,
};

const useLocaleStore = create(
  persist(
    (set, get) => ({
      // State
      country: 'PT',
      currency: 'EUR',
      language: 'pt',
      detectedCountry: null,
      exchangeRates: EXCHANGE_RATES,
      customPrices: {}, // Prices fetched from backend for different countries
      // True once the user explicitly chose a language/country/currency via the UI.
      // We use this so GeoIP detection doesn't override an explicit user choice on revisits.
      userOverriddenLocale: false,

      // Actions
      setCountry: (country) => {
        const countryConfig = COUNTRIES[country];
        if (countryConfig) {
          i18n.changeLanguage(countryConfig.language);
          set({
            country,
            currency: countryConfig.currency,
            language: countryConfig.language,
            userOverriddenLocale: true,
          });
        }
      },

      setCurrency: (currency) => {
        if (CURRENCIES[currency]) {
          set({ currency, userOverriddenLocale: true });
        }
      },

      setLanguage: (language) => {
        if (LANGUAGES[language]) {
          i18n.changeLanguage(language);
          set({ language, userOverriddenLocale: true });
        }
      },

      setDetectedCountry: (country) => {
        set({ detectedCountry: country });
        // Auto-set country/currency only when the user hasn't explicitly chosen.
        // We intentionally do NOT override `language` here: the navigator-based
        // detector inside i18next is more reliable for the user's real language
        // preference (a Portuguese-speaking user behind a US VPN/cluster IP would
        // otherwise get the page forced to English). Language stays in sync with
        // the browser/i18n unless the user explicitly switches via the selector.
        const state = get();
        if (!state.userOverriddenLocale) {
          const countryConfig = COUNTRIES[country];
          if (countryConfig) {
            set({
              country,
              currency: countryConfig.currency,
              // language intentionally NOT changed here — see comment above.
            });
          }
        }
      },
      
      setCustomPrices: (prices) => {
        set({ customPrices: prices });
      },
      
      setExchangeRates: (rates) => {
        set({ exchangeRates: rates });
      },
      
      // Helpers
      formatPrice: (priceInEUR, options = {}) => {
        const state = get();
        const { currency, exchangeRates, customPrices } = state;
        const currencyConfig = CURRENCIES[currency];
        
        // Check if there's a custom price for the current country
        const { planId, countryCode } = options;
        if (planId && customPrices[countryCode || state.country]?.[planId]) {
          const customPrice = customPrices[countryCode || state.country][planId];
          return formatCurrency(customPrice, currencyConfig);
        }
        
        // Convert from EUR
        const rate = exchangeRates[currency] || 1;
        const convertedPrice = priceInEUR * rate;
        
        return formatCurrency(convertedPrice, currencyConfig);
      },
      
      getPrice: (priceInEUR, options = {}) => {
        const state = get();
        const { currency, exchangeRates, customPrices } = state;
        
        // Check for custom price
        const { planId, countryCode } = options;
        if (planId && customPrices[countryCode || state.country]?.[planId]) {
          return customPrices[countryCode || state.country][planId];
        }
        
        // Convert from EUR
        const rate = exchangeRates[currency] || 1;
        return Math.round(priceInEUR * rate * 100) / 100;
      },
      
      getCurrencySymbol: () => {
        const state = get();
        return CURRENCIES[state.currency]?.symbol || '€';
      },
    }),
    {
      name: 'locale-store',
      partialize: (state) => ({
        country: state.country,
        currency: state.currency,
        language: state.language,
        userOverriddenLocale: state.userOverriddenLocale,
      }),
    }
  )
);

// Helper function to format currency
function formatCurrency(amount, currencyConfig) {
  const formatted = amount.toFixed(2);
  if (currencyConfig.position === 'before') {
    return `${currencyConfig.symbol}${formatted}`;
  }
  return `${formatted}${currencyConfig.symbol}`;
}

export default useLocaleStore;
